import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const BusinessFormation = ({ onGetStarted }) => {
  const benefits = [
    'Entity selection and structuring',
    'Articles of incorporation/organization filing',
    'Operating agreements and bylaws',
    'EIN obtainment and tax registration',
    'Corporate governance setup',
    'Compliance planning and guidance',
  ];

  const process = [
    { title: 'Business Structure Consultation', description: 'Assess your needs and recommend optimal entity type for your business.' },
    { title: 'Document Preparation', description: 'Draft all necessary formation documents including articles and agreements.' },
    { title: 'State Filing', description: 'File formation documents with state authorities and obtain certifications.' },
    { title: 'Corporate Setup', description: 'Establish governance framework and ensure compliance requirements are met.' },
  ];

  const entityTypes = [
    'Limited Liability Company (LLC)',
    'C Corporation',
    'S Corporation',
    'Partnership (General & Limited)',
    'Sole Proprietorship',
    'Professional Corporation',
    'Benefit Corporation',
    'Non-Profit Corporation',
  ];

  const whyChoose = [
    { icon: Award, title: 'Formation Expertise', description: 'Extensive experience forming businesses across all entity types.' },
    { icon: Shield, title: 'Liability Protection', description: 'Proper structuring to maximize personal liability protection.' },
    { icon: Clock, title: 'Fast Processing', description: 'Efficient filing process to get your business operational quickly.' },
    { icon: TrendingUp, title: 'Tax Optimization', description: 'Entity selection strategies for optimal tax treatment.' },
  ];

  const faqs = [
    { question: 'Which business entity is right for me?', answer: 'The best entity depends on your business goals, liability concerns, tax situation, and growth plans. LLCs offer flexibility and liability protection, while corporations provide more structure and investor appeal. We analyze your specific needs to recommend the optimal structure.' },
    { question: 'How long does business formation take?', answer: 'Timeline varies by state and entity type. Most formations are completed within 1-3 weeks. Expedited filing options are available in many jurisdictions for faster processing, sometimes as quick as 24-48 hours.' },
    { question: 'What is the difference between an LLC and a corporation?', answer: 'LLCs offer simpler management structure, flexible taxation, and less formality. Corporations have more rigid structure, double taxation (unless S Corp election), but better for raising investment capital and going public. Each has distinct advantages depending on your goals.' },
    { question: 'Do I need an operating agreement or bylaws?', answer: 'Yes, these documents are essential even when not legally required. They establish ownership structure, management rules, profit distribution, and dispute resolution procedures. Properly drafted agreements prevent conflicts and protect all members.' },
    { question: 'Can I change my business entity type later?', answer: 'Yes, you can convert from one entity type to another through various legal mechanisms. However, conversions can have tax implications and require proper legal procedures. We assist with entity conversions when business needs change.' },
  ];

  return (
    <div className="pt-20" data-testid="service-business-formation">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7413915/pexels-photo-7413915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Business Law Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Corporate & Business Formation</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Start your business on solid legal footing with expert entity formation and structuring services.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium" data-testid="service-cta-button">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Choosing the right business structure is crucial for liability protection, tax optimization, and future growth. Our attorneys guide you through entity selection, formation, and compliance to set your business up for success.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">We handle all aspects of business formation from state filings to corporate governance setup.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.pexels.com/photos/7433847/pexels-photo-7433847.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Business formation planning" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Entity Types We Form</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">We form all types of business entities to meet your specific needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {entityTypes.map((type, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{type}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">A streamlined approach to business formation that ensures quality and efficiency.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1758518729240-7162d07427b8?w=800&q=80" alt="Why choose our business formation team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Ready to Form Your Business?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for professional business formation services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default BusinessFormation;
